import { useState, useEffect, useCallback } from 'react';
import { Select, message } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import seasonsServiceInstance, { type Season } from '../../api/seasonsService';

interface SeasonSelectorProps {
  value?: number;
  onChange?: (year: number, season: Season) => void;
  style?: React.CSSProperties;
  placeholder?: string;
}

export const SeasonSelector: React.FC<SeasonSelectorProps> = ({
  value,
  onChange,
  style,
  placeholder = 'Select Season',
}) => {
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number | undefined>(value);

  const fetchSeasons = useCallback(async () => {
    setLoading(true);
    try {
      const response = await seasonsServiceInstance.getAllSeasons();
      
      // The executer automatically extracts response.data.data, so response.data is already the array
      if (response.success && response.data && Array.isArray(response.data)) {
        const seasonsData = response.data;
        setSeasons(seasonsData);
        
        // Auto-select season: prefer value prop, fallback to most recent season
        if (!value && seasonsData.length > 0) {
          const mostRecentSeason = seasonsData[0];
          setSelectedYear(mostRecentSeason.year);
          onChange?.(mostRecentSeason.year, mostRecentSeason);
        } else if (value) {
          // If value is provided (from localStorage), ensure it's valid
          const matchingSeason = seasonsData.find(s => s.year === value);
          if (matchingSeason) {
            setSelectedYear(matchingSeason.year);
          } else if (seasonsData.length > 0) {
            // If stored value is no longer valid, fallback to most recent
            const mostRecentSeason = seasonsData[0];
            setSelectedYear(mostRecentSeason.year);
            onChange?.(mostRecentSeason.year, mostRecentSeason);
          }
        }
      } else {
        message.error('Failed to load seasons');
      }
    } catch (error) {
      console.error('Error fetching seasons:', error);
      message.error('Error loading seasons. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, [value, onChange]);

  // Fetch seasons on component mount
  useEffect(() => {
    fetchSeasons();
  }, [fetchSeasons]);

  // Update selected year when value prop changes
  useEffect(() => {
    setSelectedYear(value);
  }, [value]);

  const handleChange = (year: number) => {
    const season = seasons.find((s) => s.year === year);
    if (season) {
      setSelectedYear(year);
      onChange?.(year, season);
    }
  };

  return (
    <Select
      value={selectedYear}
      onChange={handleChange}
      loading={loading}
      placeholder={placeholder}
      style={{ minWidth: 100, ...style }}
      suffixIcon={<CalendarOutlined />}
      options={seasons.map((season) => ({
        value: season.year,
        label: season.name,
      }))}
    />
  );
};

export default SeasonSelector;

